// PolicyGate.js
// Toont het acceptatiescherm voor de Product Terms / DPA voordat de app laadt.
// De backend bepaalt of acceptatie nodig is (alleen rollen User, Tester, Manager).
// De controle gebeurt alleen direct na het inloggen: LoginScreen zet de vlag
// POLICY_CHECK_KEY, die blijft staan tot de check klaar is of er geaccepteerd is.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import apiClient from "../utils/apiClient";
import { clearSession, POLICY_CHECK_KEY } from "../utils/session";
import "./PolicyGate.css";

// "2026-10-01" -> "1 October 2026"
const formatLongDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });

function PolicyGate({ children }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(() =>
    localStorage.getItem(POLICY_CHECK_KEY) ? null : { required: false }
  ); // null = laden
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [leaving, setLeaving] = useState(null); // "accept" | "logout" — actie na de fade-out

  useEffect(() => {
    if (!localStorage.getItem(POLICY_CHECK_KEY)) return;
    let cancelled = false;
    apiClient.get("/policies/status")
      .then(res => {
        if (!res.data.required) localStorage.removeItem(POLICY_CHECK_KEY);
        if (!cancelled) setStatus(res.data);
      })
      .catch(err => {
        // Bij een fout de app niet blokkeren
        console.error("Policy status error:", err);
        if (!cancelled) setStatus({ required: false });
      });
    return () => { cancelled = true; };
  }, []);

  if (status === null) return null;
  if (!status.required) return children;

  const isUpdate = status.type === "updated";

  const handleAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post("/policies/accept");
      localStorage.removeItem(POLICY_CHECK_KEY);
      setLeaving("accept");
    } catch (err) {
      console.error("Policy accept error:", err);
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    setLeaving("logout");
  };

  const handleFadeOutComplete = () => {
    if (leaving === "accept") {
      setStatus({ ...status, required: false });
    } else if (leaving === "logout") {
      clearSession();
      navigate("/login");
    }
  };

  return (
    <div className="policy-screen">
      <motion.div
        className="policy-container"
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={leaving ? { opacity: 0, y: -16, scale: 0.98 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: leaving ? 0.25 : 0.4, ease: [0.4, 0, 0.2, 1] }}
        onAnimationComplete={() => { if (leaving) handleFadeOutComplete(); }}
      >
        <div className="policy-intro">
          <span className="policy-header">
            {isUpdate ? "We've updated our terms" : "Before you get started"}
          </span>
          <span className="policy-sub-header">
            {isUpdate
              ? <>{status.versionDate ? `From ${formatLongDate(status.versionDate)}, updated` : "Updated"} terms apply to your use of Cadmus.</>
              : "To use Cadmus for your organisation, please review and accept our terms."}
          </span>
        </div>

        {isUpdate && status.changes?.length > 0 && (
          <div className="policy-section">
            <span className="policy-section-title">What has changed:</span>
            <ul className="policy-changes">
              {status.changes.map((change, i) => <li key={i}>{change}</li>)}
            </ul>
          </div>
        )}

        <div className="policy-divider" />

        <div className="policy-section">
          <span className="policy-label">
            Full documents{status.version ? ` (Version ${status.version})` : ""}
          </span>
          <ul className="policy-documents">
            <li><a href={status.links.productTerms} target="_blank" rel="noopener noreferrer">Product Terms</a></li>
            <li><a href={status.links.dpa} target="_blank" rel="noopener noreferrer">DPA</a></li>
          </ul>
        </div>

        <label className="policy-checkbox">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span>
            I accept the {isUpdate ? "updated " : ""}Product Terms and DPA for my organisation,
            and I'm authorised to do so.
          </span>
        </label>

        {!isUpdate && (
          <span className="policy-label">
            Read how we handle your data in our{" "}
            <a href={status.links.privacyPolicy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        )}

        {error && <div className="error-message">{error}</div>}

        <button className="button-primary policy-accept" onClick={handleAccept} disabled={!checked || submitting}>
          Accept and continue
        </button>

        <span className="policy-logout">
          or <a href="/login" onClick={handleLogout}>logout</a>
        </span>
      </motion.div>
    </div>
  );
}

export default PolicyGate;
